"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { PrismicRichText } from "@prismicio/react";
import type { RichTextField } from "@prismicio/client";
import { motion } from "@/lib/motion";
import { scrollToId } from "@/lib/utils";
import { KYC_INTRO, SECTIONS, type PrismicBlock, type RichTextNode } from "./data";
import { HeroGlow } from "@/components/ui";

interface Props {
  blocks?: PrismicBlock[];
  intro?: RichTextNode[];
}

function slugify(heading: string): string {
  return heading
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export default function KycSection({ blocks, intro }: Props) {
  const sections = blocks?.length
    ? blocks.map((b) => ({
        id: slugify(b.heading ?? ""),
        heading: b.heading ?? "",
        body: b.body,
        tableHtml: b.table_html,
        content: undefined as React.ReactNode,
      }))
    : SECTIONS.map((s) => ({ ...s, body: undefined, tableHtml: undefined }));

  const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? "");

  useEffect(() => {
    const OFFSET = 120;

    const update = () => {
      let current = sections[0]?.id ?? "";
      for (const { id } of sections) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= OFFSET) {
          current = id;
        }
      }
      setActiveId(current);
    };

    window.addEventListener("scroll", update, { passive: true });
    update();
    return () => window.removeEventListener("scroll", update);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main
      id="main-content"
      tabIndex={-1}
      className="relative isolate min-h-screen overflow-y-clip"
      style={{ background: "var(--background)" }}
    >
      {/* These pages have no hero section — HeroGlow bounds itself to a band at
          the top of the document, so it lands where it does everywhere else. */}
      <HeroGlow align="left" behind />

      <div className="relative mx-auto max-w-7xl px-4 pt-24 pb-12 sm:px-6 lg:px-8 lg:pt-32 lg:pb-16">
        <div className="lg:grid lg:grid-cols-[240px_1fr] lg:gap-16 xl:gap-20">
          <aside className="hidden lg:block">
            <div className="sticky top-20 flex max-h-[calc(100vh-6rem)] flex-col">
              <div
                className="mb-6 shrink-0 pb-5"
                style={{ borderBottom: "1px solid var(--border)" }}
              >
                <Link
                  href="/legal"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold transition-colors"
                  style={{ color: "var(--foreground)" }}
                  onMouseEnter={(e) =>
                    ((e.currentTarget as HTMLElement).style.color = "var(--muted-foreground)")
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as HTMLElement).style.color = "var(--foreground)")
                  }
                >
                  <svg
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    className="h-4 w-4 shrink-0"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13 8H3m0 0l4-4M3 8l4 4"
                    />
                  </svg>
                  Legal
                </Link>
              </div>

              <nav aria-label="Table of contents" className="flex min-h-0 flex-1 flex-col">
                <p
                  className="mb-3 shrink-0 text-[11px] font-semibold tracking-widest uppercase"
                  style={{ color: "var(--muted-foreground)" }}
                >
                  On this page
                </p>
                <ul className="toc-scroll min-h-0 flex-1 space-y-0.5 overflow-y-auto pr-1">
                  {sections.map((section) => {
                    const isActive = activeId === section.id;
                    return (
                      <li key={section.id}>
                        <button
                          onClick={() => scrollToId(section.id)}
                          className="w-full rounded-md px-3 py-1.5 text-left text-sm leading-snug transition-all duration-150"
                          style={{
                            color: isActive ? "var(--foreground)" : "var(--muted-foreground)",
                            background: isActive ? "rgba(247,247,248,0.07)" : "transparent",
                            fontWeight: isActive ? 500 : 400,
                            borderLeft: isActive
                              ? "2px solid var(--foreground)"
                              : "2px solid transparent",
                          }}
                        >
                          {section.heading}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </div>
          </aside>

          <motion.article
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="min-w-0"
          >
            <header className="mb-10 pb-8" style={{ borderBottom: "1px solid var(--border)" }}>
              <h1
                className="mb-3 text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl"
                style={{ color: "var(--foreground)" }}
              >
                Know Your Customer (KYC) Verification
              </h1>
              <div className="legal-prose text-sm" style={{ color: "var(--muted-foreground)" }}>
                {intro?.length ? (
                  <PrismicRichText field={intro as unknown as RichTextField} />
                ) : (
                  KYC_INTRO
                )}
              </div>
            </header>

            <details
              className="mb-8 overflow-hidden rounded-xl lg:hidden"
              style={{ background: "var(--surface-1)", border: "1px solid var(--border)" }}
            >
              <summary
                className="cursor-pointer px-5 py-3 text-sm font-medium select-none"
                style={{ color: "var(--foreground)" }}
              >
                On this page
              </summary>
              <ul className="space-y-0.5 px-3 pb-3">
                {sections.map((section) => (
                  <li key={section.id}>
                    <button
                      onClick={() => scrollToId(section.id)}
                      className="w-full rounded-md px-3 py-1.5 text-left text-sm transition-colors"
                      style={{ color: "var(--muted-foreground)" }}
                    >
                      {section.heading}
                    </button>
                  </li>
                ))}
              </ul>
            </details>

            <div className="space-y-12">
              {sections.map((section) => (
                <section key={section.id} id={section.id} className="scroll-mt-28">
                  <h2
                    className="mb-4 text-xl leading-snug font-semibold"
                    style={{ color: "var(--foreground)" }}
                  >
                    {section.heading}
                  </h2>
                  <div className="legal-prose">
                    {section.tableHtml && (
                      <div
                        className="mb-6 overflow-x-auto"
                        dangerouslySetInnerHTML={{ __html: section.tableHtml }}
                      />
                    )}
                    {section.body ? (
                      <PrismicRichText
                        field={section.body as unknown as RichTextField}
                        components={{
                          paragraph: ({ children }) => (
                            <p
                              className="mb-4 text-[15px] leading-[1.8] last:mb-0"
                              style={{ color: "var(--muted-foreground)" }}
                            >
                              {children}
                            </p>
                          ),
                          list: ({ children }) => (
                            <ul className="mb-4 space-y-2 last:mb-0">{children}</ul>
                          ),
                          listItem: ({ children }) => (
                            <li
                              className="flex gap-3 text-[15px] leading-[1.8]"
                              style={{ color: "var(--muted-foreground)" }}
                            >
                              <span
                                className="mt-[11px] h-1 w-1 flex-none shrink-0 rounded-full"
                                style={{ background: "var(--muted-foreground)", opacity: 0.5 }}
                              />
                              <span>{children}</span>
                            </li>
                          ),
                          oList: ({ children }) => (
                            <ol
                              className="mb-4 list-inside list-decimal space-y-2 text-[15px] leading-[1.8] last:mb-0"
                              style={{ color: "var(--muted-foreground)" }}
                            >
                              {children}
                            </ol>
                          ),
                          oListItem: ({ children }) => (
                            <li
                              className="ml-4 text-[15px] leading-[1.8]"
                              style={{ color: "var(--muted-foreground)" }}
                            >
                              {children}
                            </li>
                          ),
                          heading3: ({ children }) => (
                            <h3
                              className="mt-6 mb-2 text-lg font-semibold first:mt-0"
                              style={{ color: "var(--foreground)" }}
                            >
                              {children}
                            </h3>
                          ),
                          strong: ({ children }) => (
                            <strong
                              className="font-semibold"
                              style={{ color: "var(--foreground)" }}
                            >
                              {children}
                            </strong>
                          ),
                          hyperlink: ({ children, node }) => {
                            const url = (node.data as { url?: string }).url ?? "#";
                            const isExternal =
                              !url.includes("proxidize.com") && !url.startsWith("/");
                            return (
                              <a
                                href={url}
                                className="transition-colors"
                                style={{
                                  color: "var(--foreground)",
                                  textDecoration: "underline",
                                  textUnderlineOffset: "3px",
                                }}
                                {...(isExternal
                                  ? { target: "_blank", rel: "noopener noreferrer" }
                                  : { rel: "noopener" })}
                              >
                                {children}
                              </a>
                            );
                          },
                        }}
                      />
                    ) : (
                      section.content
                    )}
                  </div>
                </section>
              ))}
            </div>

            <div
              className="mt-16 hidden pt-8 sm:flex"
              style={{ borderTop: "1px solid var(--border)" }}
            >
              <Link
                href="/legal"
                className="inline-flex shrink-0 items-center gap-2 text-sm transition-colors"
                style={{ color: "var(--muted-foreground)" }}
                onMouseEnter={(e) =>
                  ((e.currentTarget as HTMLElement).style.color = "var(--foreground)")
                }
                onMouseLeave={(e) =>
                  ((e.currentTarget as HTMLElement).style.color = "var(--muted-foreground)")
                }
              >
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  className="h-4 w-4"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 8H3m0 0l4-4M3 8l4 4" />
                </svg>
                Legal documents
              </Link>
            </div>
          </motion.article>
        </div>
      </div>
    </main>
  );
}
